import {FC, memo, ReactNode, useEffect, useMemo, useState} from "react";
import ReactMarkdown, {defaultUrlTransform, Options} from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import mermaid from "mermaid";
import {v4 as uuid} from "uuid";
import {Dialog, DialogContent, DialogTitle, DialogTrigger} from "@/components/ui/dialog";
import {cn} from "@/lib/utils";
import {Code2, ZoomIn} from "lucide-react";
import {Skeleton} from "@/components/ui/skeleton";

function initializeMermaid() {
  mermaid.initialize({startOnLoad: false, theme: "default"});
  mermaid.setParseErrorHandler(() => undefined);
}

function extractText(node: ReactNode): string {
  if (typeof node === 'string') return node
  if (typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(extractText).join('')
  if (node && typeof node === 'object' && 'props' in node) {
    return extractText((node as React.ReactElement<{children?: ReactNode}>).props.children)
  }
  return ''
}

export function slugify(text: string): string {
  return text.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').replace(/^-+|-+$/g, '')
}

function HeadingWithId({level, children, ...props}: {level: number, children?: ReactNode, [k: string]: unknown}) {
  const Tag = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  const id = slugify(extractText(children))
  return <Tag id={id} {...props}>{children}</Tag>
}

const MemoizedReactMarkdown: FC<Options> = memo(
  ReactMarkdown,
  (prevProps, nextProps) => prevProps.children === nextProps.children,
);

export function createMarkdownComponents(components?: Options["components"]): Options["components"] {
  return {
    h1: ({children, ...props}) => <HeadingWithId level={1} {...props}>{children}</HeadingWithId>,
    h2: ({children, ...props}) => <HeadingWithId level={2} {...props}>{children}</HeadingWithId>,
    h3: ({children, ...props}) => <HeadingWithId level={3} {...props}>{children}</HeadingWithId>,
    h4: ({children, ...props}) => <HeadingWithId level={4} {...props}>{children}</HeadingWithId>,
    h5: ({children, ...props}) => <HeadingWithId level={5} {...props}>{children}</HeadingWithId>,
    h6: ({children, ...props}) => <HeadingWithId level={6} {...props}>{children}</HeadingWithId>,
    a: ({href, children, ...props}) => {
      const isPageAnchor = href?.startsWith("#");
      return <a
        href={href}
        target={isPageAnchor ? undefined : "_blank"}
        rel={isPageAnchor ? undefined : "noopener noreferrer"}
        {...props}
      >
        {children}
      </a>;
    },
    pre({children, ...rest}) {
      // Check if this pre contains a code element with mermaid
      const child = Array.isArray(children) ? children[0] : children;
      if (child?.props?.className?.includes('language-mermaid')) {
        return <MermaidBlock content={child.props.children}/>;
      }
      return <pre {...rest}>{children}</pre>;
    },
    code({className, children, ...rest}) {
      const lang = className ? className.replace("language-", "") : "";
      if (lang === "mermaid") {
        return <MermaidBlock content={children}/>;
      }
      return <code className={className} {...rest}>
        {children}
      </code>
    },
    ...components,
  };
}

export const Markdown = ({
  content,
  className,
  components,
  urlTransform = defaultUrlTransform,
}: {
  content: string;
  className?: string;
  components?: Options["components"];
  urlTransform?: Options["urlTransform"];
}) => {
  useEffect(() => {
    initializeMermaid();
  }, [content]);
  return <div className={cn("prose dark:prose-invert prose-sm w-full text-sm max-w-full", className)}>
    <MemoizedReactMarkdown
      remarkPlugins={[remarkGfm, remarkMath]}
      // rehypePlugins={[rehypeRaw]}
      components={createMarkdownComponents(components)}
      urlTransform={urlTransform}
    >
      {content}
    </MemoizedReactMarkdown>
  </div>
};

const MermaidBlock = ({content}: { content: ReactNode }) => {
  useEffect(() => {
    initializeMermaid();
  }, []);

  return <div className="border rounded p-2 my-4">
    <div className="flex items-center justify-end gap-2 text-sm">
      <Dialog>
        <DialogTrigger
          className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label="Zoom diagram"
          title="Zoom diagram"
        >
          <ZoomIn className="size-4"/>
        </DialogTrigger>
        <DialogContent className="max-h-[90vh] w-[90vw] max-w-[90vw] overflow-auto sm:max-w-[90vw]">
          <DialogTitle>Diagram</DialogTitle>
          <div className="min-w-0 overflow-auto">
            <MermaidDiagram content={content} className="min-w-max"/>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog>
        <DialogTrigger
          className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label="Show Mermaid source"
          title="Show Mermaid source"
        >
          <Code2 className="size-4"/>
        </DialogTrigger>
        <DialogContent>
          <DialogTitle>Mermaid as markdown</DialogTitle>
          <div className="w-full overflow-scroll prose dark:prose-invert prose-sm text-sm">
            <pre>
              {String(content)}
            </pre>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    <MermaidDiagram content={content}/>
  </div>
};

const MermaidDiagram = ({content, className}: {content: ReactNode; className?: string}) => {
  const diagram = useMemo(() => String(content).trim(), [content]);
  const [svg, setSvg] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSvg(null);
    setFailed(false);

    const timer = setTimeout(() => {
      const renderID = `mermaid-${uuid()}`;
      void mermaid.parse(diagram, {suppressErrors: true})
        .then(parseResult => {
          if (!parseResult) return null;
          return mermaid.render(renderID, diagram);
        })
        .then(result => {
          if (cancelled) return;
          if (result) {
            setSvg(result.svg);
          } else {
            setFailed(true);
          }
        })
        .catch(() => {
          if (!cancelled) setFailed(true);
        });
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [diagram]);

  if (failed) {
    return <MermaidFallback diagram={diagram} className={className}/>;
  }

  if (!svg) {
    return <MermaidSkeleton className={className}/>;
  }

  return <div className={className} dangerouslySetInnerHTML={{__html: svg}}/>;
};

const MermaidFallback = ({diagram, className}: {diagram: string; className?: string}) => {
  return (
    <pre className={cn("not-prose max-h-80 overflow-auto rounded-md border border-border/70 bg-muted/40 p-3 text-xs", className)}>
      <code>{diagram}</code>
    </pre>
  );
};

const MermaidSkeleton = ({className}: {className?: string}) => {
  return (
    <div
      className={cn("not-prose w-full rounded-md border border-border/70 p-4", className)}
      aria-label="Rendering diagram"
    >
      <div className="mx-auto flex min-h-40 max-w-2xl items-center justify-center">
        <div className="grid w-full grid-cols-[1fr_auto_1fr] items-center gap-3">
          <Skeleton className="h-12 rounded-md"/>
          <Skeleton className="size-3 rounded-full"/>
          <Skeleton className="h-12 rounded-md"/>
          <Skeleton className="col-span-3 mx-auto h-px w-2/3"/>
          <Skeleton className="col-span-3 mx-auto h-12 w-1/2 rounded-md"/>
        </div>
      </div>
    </div>
  );
};
